/**
 * Copyright Highway9 Networks Inc.
 * Notification styles for the notification panel
 * Toast notifications are handled by notistack
 */
/**
 * Notification panel styles
 */
import { SxProps, Theme } from '@h9/ui-lib';
export const notificationPanelStyles = {
  popover: {
    '& .MuiPopover-paper': {
      width: 420,
      maxWidth: '90vw',
      maxHeight: '70vh',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      borderRadius: 2,
      overflow: 'hidden',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 2,
    py: 1.5,
    borderBottom: 1,
    borderColor: 'divider',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.025rem',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '70vh',
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 2,
    py: 1,
    borderBottom: 1,
    borderColor: 'divider',
    gap: 2,
    bgcolor: 'background.paper',
  },
  listContainer: {
    maxHeight: 'calc(70vh - 200px)',
    minHeight: '300px',
    overflow: 'auto',
  },
  listSubheader: {
    bgcolor: 'background.default',
    color: 'text.secondary',
    fontWeight: 600,
    lineHeight: 2.2,
  },
  listItem: {
    cursor: 'pointer',
    borderBottom: 1,
    borderColor: 'divider',
    alignItems: 'flex-start',
    '&:hover': {
      bgcolor: 'action.hover',
    },
  },
  listItemIcon: {
    minWidth: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: 2,
    p: 4,
    textAlign: 'center',
    minHeight: '300px',
  },
  emptyIcon: {
    fontSize: 64,
    color: 'text.disabled',
  },
  footer: {
    p: 1.5,
    borderTop: 1,
    borderColor: 'divider',
    display: 'flex',
    gap: 1,
    bgcolor: 'background.paper',
  },
  notificationHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  notificationBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    mt: 0.5,
  },
  notificationActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 1,
  },
  actionButtonsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
  },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Notification bell icon styles
 */
export const notificationBellIconStyles = {
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
    verticalAlign: 'middle',
  },
  iconButton: {
    transition: 'transform 0.2s ease-in-out',
  },
  iconButtonPulsing: {
    animation: 'pulse 0.6s ease-in-out',
    '@keyframes pulse': {
      '0%, 100%': {
        transform: 'scale(1)',
      },
      '50%': {
        transform: 'scale(1.1)',
      },
    },
  },
  collapse: {
    display: 'inline-flex',
    ml: 1,
    verticalAlign: 'middle',
  },
  extension: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    px: 1.25,
    py: 0.5,
    boxShadow: 2,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    height: '32px',
    whiteSpace: 'nowrap',
    '&:hover': {
      boxShadow: 4,
      borderColor: 'primary.main',
    },
  },
  statusIconContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  title: {
    fontSize: '0.8125rem',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px',
    color: 'text.primary',
  },
  progress: {
    fontSize: '0.7rem',
    ml: -0.5,
  },
} satisfies Record<string, SxProps<Theme>>;

